import { toast } from '@bozic/ui/toast';
import { all, call, debounce, put, select, takeLatest } from 'redux-saga/effects';
import { getType, type ActionType } from 'typesafe-actions';

import { getErrorMessage } from '@/utils/invariant';

import insuranceAPI from '../../services/insuranceService';
import { loadersActions } from '../loaders/actions';
import { getOperationName } from '../operationPage/selectors';

import { projectionDataActions } from './actions';
import { getProjectionDataNextYearSettings } from './selectors';

import type { ProjectionAnalysisDataDto } from '../../types/dto/ProjectionAnalysisDataDto';
import type { ProjectionAnalysisParametersDto } from '../../types/dto/ProjectionAnalysisParametersDto';
import type { LegacySagaGenerator } from '../legacySagaGenerator';
import type { AxiosResponse } from 'axios';

function* getProjectionDataProjectionModel(
  action: ActionType<typeof projectionDataActions.getProjectionModel>
): LegacySagaGenerator {
  try {
    yield put(loadersActions.revenueProjection.activate());

    const operationName = yield select(getOperationName);
    const response: AxiosResponse<ProjectionAnalysisDataDto> = yield call(
      insuranceAPI.getProjectedAnalysis,
      operationName,
      action.payload,
      { projectionModel: action.meta }
    );

    toast.success(
      'You changed Revenue Projection Model. The basis is calculated based on historical data. If you want to save this data select Save.',
      { position: 'top-right' }
    );

    yield put(projectionDataActions.save(response.data));
    yield put(loadersActions.revenueProjection.disable());
  } catch (error) {
    toast.error(getErrorMessage(error));
  }
}

function* getProjectionData(
  action: ActionType<typeof projectionDataActions.get>
): LegacySagaGenerator {
  try {
    yield put(loadersActions.revenueProjection.activate());
    const operationName = yield select(getOperationName);
    const response: AxiosResponse<ProjectionAnalysisDataDto> = yield call(
      insuranceAPI.getProjectedAnalysis,
      operationName,
      action.payload,
      action.meta
    );

    const nextYearSettings = yield select(getProjectionDataNextYearSettings);
    const nextYearResponse: AxiosResponse<ProjectionAnalysisDataDto> = yield call(
      insuranceAPI.getProjectedAnalysis,
      operationName,
      action.payload + 1,
      nextYearSettings || {}
    );

    yield put(projectionDataActions.save(response.data));
    yield put(projectionDataActions.nextYearSave(nextYearResponse.data));
    yield put(loadersActions.revenueProjection.disable());
  } catch (error) {
    toast.error(getErrorMessage(error));
  }
}

function* getProjectionDataForSpecificYear(
  action: ActionType<typeof projectionDataActions.getOnBlur>
): LegacySagaGenerator {
  try {
    yield put(loadersActions.revenueProjection.activate());
    const operationName = yield select(getOperationName);
    const response: AxiosResponse<ProjectionAnalysisDataDto> = yield call(
      insuranceAPI.getProjectedAnalysis,
      operationName,
      action.payload,
      action.meta
    );

    yield put(projectionDataActions.save(response.data));
    yield put(loadersActions.revenueProjection.disable());
  } catch (error) {
    toast.error(getErrorMessage(error));
  }
}

function* getProjectionSettings(
  action: ActionType<typeof projectionDataActions.settings.get>
): LegacySagaGenerator {
  try {
    yield put(loadersActions.revenueProjection.activate());

    const operationName = yield select(getOperationName);

    const nextYearResponse: AxiosResponse<ProjectionAnalysisParametersDto> = yield call(
      insuranceAPI.getProjectedAnalysisSettings,
      operationName,
      action.payload + 1
    );
    if (nextYearResponse.status === 204) {
      yield put(projectionDataActions.settings.nextYearSave(null));
    } else {
      yield put(projectionDataActions.settings.nextYearSave(nextYearResponse.data));
    }

    const response: AxiosResponse<ProjectionAnalysisParametersDto> = yield call(
      insuranceAPI.getProjectedAnalysisSettings,
      operationName,
      action.payload
    );
    if (response.status === 204) {
      yield put(projectionDataActions.settings.save(null));
      yield put(projectionDataActions.get(action.payload, {}));
    } else {
      const { year, ...params } = response.data;
      yield put(projectionDataActions.settings.save({ year, ...params }));
      yield put(projectionDataActions.get(year, params));
    }
  } catch (error) {
    toast.error(getErrorMessage(error), {
      position: 'top-right'
    });
  }
}

function* updateProjectionSettings(
  action: ActionType<typeof projectionDataActions.settings.update>
): LegacySagaGenerator {
  try {
    const operationName = yield select(getOperationName);
    yield call(insuranceAPI.updateProjectedAnalysisSettings, operationName, action.payload);

    const { year, ...params } = action.payload;
    yield put(projectionDataActions.settings.save({ year, ...params }));

    yield toast.success('Changes Saved!', {
      position: 'top-right'
    });

    yield put(projectionDataActions.get(year, params));
  } catch (error) {
    toast.error(getErrorMessage(error), {
      position: 'top-right'
    });
  }
}

function* projectionDataSagaWatcher() {
  yield all([
    debounce(
      800,
      getType(projectionDataActions.getProjectionModel),
      getProjectionDataProjectionModel
    ),
    takeLatest(getType(projectionDataActions.get), getProjectionData),
    takeLatest(getType(projectionDataActions.getOnBlur), getProjectionDataForSpecificYear),
    takeLatest(getType(projectionDataActions.settings.get), getProjectionSettings),
    takeLatest(getType(projectionDataActions.settings.update), updateProjectionSettings)
  ]);
}

export default projectionDataSagaWatcher;
